'use client'

import { FormError } from '@/components/forms/FormError'
import { FormItem } from '@/components/forms/FormItem'
import { Message } from '@/components/Message'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/providers/Auth'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import React, { useCallback, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Info } from 'lucide-react'
import { useTranslations } from 'next-intl'

type FormData = {
  name: string
  email: string
  password: string
  passwordConfirm: string
}

export const CreateAccountForm: React.FC = () => {
  const t = useTranslations('auth')
  const searchParams = useSearchParams()
  const allParams = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const { login } = useAuth()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<null | string>(null)
  const [emailCheckLoading, setEmailCheckLoading] = useState(false)
  const [emailExists, setEmailExists] = useState(false)

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    setError: setFormError,
    clearErrors,
  } = useForm<FormData>()

  const password = useRef({})
  password.current = watch('password', '')
  const emailValue = watch('email', '')

  /**
   * Check if email already exists when user types
   */
  const checkEmailExists = useCallback(
    async (email: string) => {
      if (!email || email.length < 3) {
        setEmailExists(false)
        return
      }

      // Basic email format check
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        return
      }

      setEmailCheckLoading(true)
      try {
        const response = await fetch(`/api/users/check-email?email=${encodeURIComponent(email)}`)
        const data = await response.json()

        if (data.exists) {
          setEmailExists(true)
          setFormError('email', {
            type: 'manual',
            message: t('emailAlreadyRegistered'),
          })
        } else {
          setEmailExists(false)
          clearErrors('email')
        }
      } catch (error) {
        console.error('Error checking email:', error)
      } finally {
        setEmailCheckLoading(false)
      }
    },
    [setFormError, clearErrors, t],
  )

  // Debounced email check
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (emailValue) {
        checkEmailExists(emailValue)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [emailValue, checkEmailExists])

  const onSubmit = useCallback(
    async (data: FormData) => {
      // Final check before submission
      if (emailExists) {
        setError(t('emailAlreadyRegistered'))
        return
      }

      // Normalize email
      const normalizedData = {
        ...data,
        email: data.email.toLowerCase().trim(),
      }

      const response = await fetch(`${process.env.NEXT_PUBLIC_SERVER_URL}/api/users`, {
        body: JSON.stringify(normalizedData),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const message =
          errorData?.errors?.[0]?.message ||
          errorData?.message ||
          response.statusText ||
          t('accountCreationError')
        setError(message)
        return
      }

      const redirect = searchParams.get('redirect')

      const timer = setTimeout(() => {
        setLoading(true)
      }, 1000)

      try {
        await login(normalizedData)
        clearTimeout(timer)
        if (redirect) {
          router.refresh()
          router.push(redirect)
        } else {
          router.refresh()
          router.push(`/account?success=${encodeURIComponent(t('accountCreatedSuccess'))}`)
        }
      } catch (_) {
        clearTimeout(timer)
        setError(t('credentialsErrorSignup'))
      }
    },
    [login, router, searchParams, emailExists, t],
  )

  return (
    <form className="max-w-lg py-4" onSubmit={handleSubmit(onSubmit)}>
      <Message error={error} />

      <div className="flex flex-col gap-8 mb-8">
        <FormItem>
          <Label htmlFor="name" className="mb-2">
            {t('fullName')}
          </Label>
          <Input
            id="name"
            {...register('name', {
              required: t('nameRequired'),
              minLength: {
                value: 2,
                message: t('nameMinLength'),
              },
            })}
            type="text"
            placeholder={t('namePlaceholder')}
          />
          {errors.name && <FormError message={errors.name.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="email" className="mb-2">
            {t('emailAddress')}
          </Label>
          <Input
            id="email"
            {...register('email', {
              required: t('emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('emailInvalid'),
              },
            })}
            type="email"
            placeholder={t('emailPlaceholder')}
          />
          {emailCheckLoading && <p className="text-sm text-neutral-500 mt-1">{t('checkingEmail')}</p>}
          {emailExists && !emailCheckLoading && (
            <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md p-3 flex gap-2">
              <Info className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="text-sm">
                <p className="text-amber-900 dark:text-amber-100 font-medium">
                  {t('emailAlreadyRegistered')}
                </p>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  <Link href={`/login${allParams}`} className="underline font-medium">
                    {t('logInInstead')}
                  </Link>{' '}
                  {t('orUseDifferentEmail')}
                </p>
              </div>
            </div>
          )}
          {errors.email && <FormError message={errors.email.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="password" className="mb-2">
            {t('newPasswordLabel')}
          </Label>
          <Input
            id="password"
            {...register('password', {
              required: t('passwordRequired'),
              minLength: {
                value: 8,
                message: t('passwordMinLength'),
              },
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: t('passwordRequirements'),
              },
            })}
            type="password"
          />
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            {t('passwordHint')}
          </p>
          {errors.password && <FormError message={errors.password.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="passwordConfirm" className="mb-2">
            {t('confirmPasswordLabel')}
          </Label>
          <Input
            id="passwordConfirm"
            {...register('passwordConfirm', {
              required: t('pleaseConfirmPassword'),
              validate: (value) => value === password.current || t('passwordsDoNotMatch'),
            })}
            type="password"
          />
          {errors.passwordConfirm && <FormError message={errors.passwordConfirm.message} />}
        </FormItem>
      </div>
      <Button
        disabled={loading || emailCheckLoading || emailExists}
        type="submit"
        variant="default"
      >
        {loading ? t('processingAccount') : t('createAccountButton')}
      </Button>

      <div className="prose dark:prose-invert mt-8">
        <p>
          {t('alreadyHaveAccountQuestion')}{' '}
          <Link href={`/login${allParams}`}>{t('loginHere')}</Link>
        </p>
      </div>
    </form>
  )
}
